import React from 'react'
import PropTypes from 'prop-types'
import styled, { css } from 'styled-components'
import Theme from 'components/common/Theme/Theme'
import Mixin from 'components/common/Mixin'
import Media from 'components/common/Media'
import Link, { A } from 'components/common/Link/Link'
import Button from 'components/common/Button/Button'
import Image from 'components/common/Image'
import { getOnClickAndOnKeyPressDownEvents } from 'helpers/getOnClickAndOnKeyPressDownEvents'
import { filterProps } from 'helpers/utils'
import activeStar from './assets/Icon-star-full.svg'
import inActiveStar from './assets/Icon-star-empty.svg'

const Rating = ({
  className,
  ratingCount = 0,
  reviewsLink = '',
  reviewsCount = 0,
  ratingMax = 5,
  inline,
  small,
  smallest,
  showStarsOnly,
  noReviews,
  anchor,
  prevent,
  handler = () => {},
  showRatingMax,
  hideReviewText,
  productCount = 1
}) =>
  <RatingBlock
    reviewsCount={ reviewsCount }
    productCount={ productCount }
    small={ small }
    inline={ inline }
    className={ className }
    data-at-rating>
    <RatingButton
      link
      data-at-rating-button
      productCount={ productCount }
      { ...getOnClickAndOnKeyPressDownEvents(handler) }>
      <Stars
        anchor={ anchor || !!reviewsLink }
        small={ small }
        smallest={ smallest }>
        <StarsImageContainer rating={ ratingCount }>
          <StarImage src={ activeStar } alt="" />
          <StarImage src={ activeStar } alt="" />
          <StarImage src={ activeStar } alt="" />
          <StarImage src={ activeStar } alt="" />
          <StarImage src={ activeStar } alt="" />
        </StarsImageContainer>
      </Stars>
      <RatingCount
        reviewsLink={ reviewsLink }
        small={ small }
        srOnly={ ratingCount === 0 }
        showStarsOnly={ showStarsOnly }>
        <Media.ScreenReader>
          Rating:
        </Media.ScreenReader>
        { reviewsCount ? <strong itemProp="ratingValue">{ ratingCount.toFixed(1) }</strong> : 0.0 }
        <Media.ScreenReader>
          stars out of
        </Media.ScreenReader>
        <span data-sr-only={ !showRatingMax ? 'true' : null } >
          <strong aria-hidden="true"> / </strong>
          { reviewsCount ? <strong itemProp="bestRating">{ ratingMax }</strong> : ratingMax }
        </span>
        <Media.ScreenReader>
          out of { reviewsCount ? <span itemProp="ratingCount">{ reviewsCount }</span> : 0 } Reviews
        </Media.ScreenReader>
      </RatingCount>
    </RatingButton>
    {
      anchor ?
        <ReviewsAnchor
          small={ small }
          prevent={ prevent }
          handler={ handler }
          href={ reviewsLink }
          hidden={ (ratingCount === 0 && reviewsCount === 0) || smallest || noReviews }
          showStarsOnly={ showStarsOnly }>
          { `(${reviewsCount})` }
          <ReviewsText data-at-rating-read-link hideReviewText={ hideReviewText } >
            Review{ reviewsCount !== 1 ? 's' : '' }
          </ReviewsText>
        </ReviewsAnchor> :
        <ReviewsLink
          data-review-link
          to={ reviewsLink }
          hidden={ (ratingCount === 0 && reviewsCount === 0) || smallest || noReviews }
          showStarsOnly={ showStarsOnly }
          productCount={ productCount }>
          { `(${reviewsCount})` }
          <ReviewsText hideReviewText={ hideReviewText } >
            Review{ reviewsCount !== 1 ? 's' : '' }
          </ReviewsText>
        </ReviewsLink>
    }
    {
      (ratingCount === 0 && reviewsCount === 0) && !showStarsOnly &&
        <NoReviews
          data-review-link
          small={ small }
          productCount={ productCount }>
          No Ratings
        </NoReviews>
    }
  </RatingBlock>

Rating.propTypes = {
  anchor: PropTypes.bool,
  className: PropTypes.string,
  handler: PropTypes.func,
  hideReviewText: PropTypes.bool,
  inline: PropTypes.bool,
  noReviews: PropTypes.bool,
  prevent: PropTypes.bool,
  productCount: PropTypes.number,
  ratingCount: PropTypes.number,
  ratingMax: PropTypes.number,
  reviewName: PropTypes.string,
  reviewsCount: PropTypes.number,
  reviewsLink: PropTypes.string,
  showRatingMax: PropTypes.bool,
  showStarsOnly: PropTypes.bool,
  small: PropTypes.bool,
  smallest: PropTypes.bool,
  storeRating: PropTypes.bool
}

const RatingBlock = styled.div.attrs(({
  reviewsCount
}) => ({
  itemProp: reviewsCount ? 'reviewRating' : null,
  itemScope: reviewsCount ? true : null,
  itemType: reviewsCount ? 'https://schema.org/reviewRating' : null
}))`
  display: flex;
  align-items: flex-end;
  ${Mixin.font.GothamBook(18, 400)}
  ${props => props.small && css`
    line-height: 15px;
    font-size: 14px;
  `}
  ${props => props.inline && css`
    display: inline-block;
  `}
  ${props => props.productCount === 2 && css`
    @media (max-width: 620px) {
      display: block;
    }
  `}
  ${props => props.productCount === 3 && css`
    @media (max-width: 850px) {
      display: block;
    }
  `}
`

const Stars = styled.div`
  position: relative;
  display: inline-block;
  width: 118px;
  height: 19px;
  vertical-align: middle;
  text-align: left;
  background: url(${inActiveStar}) repeat-x;
  ${props => props.anchor && css`
    cursor: pointer;
  `}
  ${props => props.small && css`
    width: 103px;
    height: 17px;
    background-size: auto 17px;
  `}
  ${Mixin.property('smallest', `
    width: 86px;
    height: 14px;
    background-size: auto 14px;
  `)}
`

const StarsImageContainer = styled.div`
  width: ${props => props.rating * 20 || '0'}%;
  height: 100%;
  overflow: hidden;
  white-space: nowrap;
`

const StarImage = styled(Image)`
  && {
    display: inline-block;
    height: 100%;
    width: auto;
  }
`

const propsToRemove = [
  'small',
  'srOnly',
  'showStarsOnly',
  'reviewsLink',
  'hidden'
]

const RatingCount = styled(props => <span { ...filterProps(props, propsToRemove) } />)`
  font-weight: bold;
  vertical-align: middle;
  margin-left: 10px;
  ${props => props.small && css`
    margin-left: 6px;
  `}
  ${props => props.srOnly && css`
    ${Mixin.srOnly()}
  `}
  ${props => props.showStarsOnly && css`
    ${Mixin.srOnly()}
  `}
  ${props => props.reviewsLink && css`
    cursor: pointer;
  `}
`

const NoReviews = styled(props => <span { ...filterProps(props, propsToRemove) } />)`
  vertical-align: middle;
  font-size: 12px;
  font-weight: 600;
  margin-left: 10px;
  line-height: 12px;
  ${props => props.small && css`
    margin-left: 13px;
  `}
  ${props => props.hidden && css`
    display: none;
  `}
  ${props => props.showStarsOnly && css`
    display: none;
  `}
  ${props => props.productCount === 2 && css`
    @media (max-width: 620px) {
      text-align: center;
      margin: 0;
    }
  `}
  ${props => props.productCount === 3 && css`
    @media (max-width: 850px) {
      text-align: center;
      margin: 0;
    }
  `}
`

const ReviewsLink = styled(props => <Link { ...filterProps(props, propsToRemove) } />)`
  position: relative;
  font-weight: 600;
  font-size: 12px;
  line-height: 12px;
  color: ${Theme.colors.lightblue};
  vertical-align: middle;
  margin-left: 10px;

  &:hover {
    text-decoration: underline;
  }

  ${props => props.hidden && css`
    display: none;
  `}
  ${props => props.showStarsOnly && css`
    display: none;
  `}
  ${props => props.productCount === 2 && css`
    @media (max-width: 620px) {
      text-align: center;
      margin: 10px auto;
    }
  `}
  ${props => props.productCount === 3 && css`
    @media (max-width: 850px) {
      text-align: center;
      margin: 10px auto;
    }
  `}
`

const ReviewsAnchor = styled(props => <A { ...filterProps(props, propsToRemove) } />)`
  position: relative;
  font-weight: 600;
  font-size: 12px;
  line-height: 12px;
  cursor: pointer;
  color: ${Theme.colors.lightblue};
  vertical-align: middle;
  margin-left: 10px;

  &:hover {
    text-decoration: underline;
  }

  ${props => props.hidden && css`
    display: none;
  `}
  ${props => props.showStarsOnly && css`
    display: none;
  `}
`

const ReviewsText = styled.span`
  padding-left: 5px;
  font-weight: 600;
  font-size: 12px;
  ${props => props.hideReviewText && css`
    display: none;
  `}
`

const RatingButton = styled(Button)`
  && {
    display: flex;
    align-items: flex-end;
    border: none;
    padding: 0;
    color: ${Theme.colors.black};
    font-weight: bold;

    &:hover {
      background: transparent;
    }
  }

  ${props => props.productCount === 2 && css`
    @media (max-width: 620px) {
      margin: 0 auto;
    }
  `}
  ${props => props.productCount === 3 && css`
    @media (max-width: 850px) {
      margin: 0 auto;
    }
  `}
`

export default Rating
